import { memo, Fragment } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from '@mui/material';
import {
  cellBaseStyle,
  subHeaderRowStyle,
  headerCellStyle,
  footerCellStyle,
  grandTotalCellStyle,
} from './reportTableStyles';

/**
 * Single Corporation Attendance & Labour Table
 * Handles Daily, Weekly, and Monthly single corporation views with expandable employee rows,
 * group totals, and grand totals.
 */
const SingleCorpTable = ({
  visibleColumns = [],
  tableGroups = [],
  calculateTotals,
  isMultiCorp = false,
  isDaily = false,
  reportType = 'daily',
  hasProfitCenter = false,
  activeOptionalColumns = [],
  footerColSpan = 2,
  allRecords = [],
  grandTotals = { scheduledHrs: 0, actualHrs: 0, overtimeHrs: 0, varianceHrs: 0 },
}) => {
  if (tableGroups.length === 0) {
    return (
      <Box sx={{ width: '100%', mt: 0 }}>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: '1px solid #d9d9d9',
            borderRadius: 0,
            overflowX: 'auto',
            backgroundColor: '#ffffff',
          }}
        >
          <Table stickyHeader sx={{ width: 'max-content', minWidth: '100%', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'auto' }}>
            <TableHead sx={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#f5f5f5' }}>
              <TableRow>
                {visibleColumns.map((col) => (
                  <TableCell
                    key={col.id}
                    align={col.align}
                    sx={{
                      ...headerCellStyle,
                      minWidth: col.minWidth,
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell
                  colSpan={visibleColumns.length}
                  align="center"
                  sx={{ ...cellBaseStyle, py: 4, textAlign: 'center' }}
                >
                  No attendance records found for this period.
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '100%', minHeight: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: '1px solid #d9d9d9',
          borderRadius: 0,
          flex: 1,
          height: '100%',
          maxHeight: '100%',
          overflow: 'auto',
          backgroundColor: '#ffffff',
        }}
      >
        <Table stickyHeader sx={{ width: 'max-content', minWidth: '100%', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'auto' }}>
          <TableHead sx={{ position: 'sticky', top: 0, zIndex: 100, backgroundColor: '#f5f5f5' }}>
            {/* Main Column Headers */}
            <TableRow>
              {visibleColumns.map((col) => (
                <TableCell
                  key={col.id}
                  align={col.align}
                  sx={{
                    ...headerCellStyle,
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    backgroundColor: '#f5f5f5',
                    minWidth: col.minWidth,
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {tableGroups.map((group, groupIdx) => {
              const groupTotals = calculateTotals ? calculateTotals(group.records) : { scheduledHrs: 0, actualHrs: 0, overtimeHrs: 0, varianceHrs: 0 };
              const isMultiDaily = isMultiCorp && isDaily;

              return (
                <Fragment key={group.id || `group-${groupIdx}`}>
                  {/* Subheader Row BELOW Table Head Column */}
                  {group.headerTitle && (
                    <TableRow>
                      <TableCell
                        colSpan={visibleColumns.length}
                        align={isMultiDaily ? 'center' : 'left'}
                        sx={
                          isMultiDaily
                            ? {
                              ...cellBaseStyle,
                              textAlign: 'center',
                              fontWeight: 'bold',
                              fontSize: '13px',
                              py: 1,
                              backgroundColor: 'transparent',
                              borderBottom: '1px solid #d9d9d9',
                            }
                            : {
                              ...subHeaderRowStyle,
                              py: 0.75,
                            }
                        }
                      >
                        {reportType === 'weekly' || reportType === 'monthly' || isMultiDaily ? (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: isMultiDaily ? 'center' : 'flex-start',
                              width: '100%',
                            }}
                          >
                            <Typography
                              component="span"
                              sx={{
                                fontWeight: 700,
                                fontSize: isMultiDaily ? '13px' : '0.85rem',
                                color: '#1e293b',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {isMultiDaily
                                ? (group.dateTitle || group.headerTitle)
                                : (group.monthTitle || group.weekTitle || group.headerTitle)}
                            </Typography>
                          </Box>
                        ) : (
                          group.headerTitle
                        )}
                      </TableCell>
                    </TableRow>
                  )}

                  {group.records.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={visibleColumns.length}
                        align="center"
                        sx={{ ...cellBaseStyle, py: 4 }}
                      >
                        No attendance records found for this period.
                      </TableCell>
                    </TableRow>
                  ) : (
                    group.records.map((row, index) => (
                      <TableRow
                        key={`${group.id || 'grp'}-row-${row.id || index}-${index}`}
                        sx={{
                          backgroundColor: '#ffffff',
                          '&:hover': {
                            backgroundColor: '#fafafa',
                          },
                        }}
                      >
                        {/* Corporation (Multi-Corp Daily) */}
                        {isMultiCorp && isDaily && (
                          <TableCell align="left" sx={cellBaseStyle}>
                            {row.corporationName || '-'}
                          </TableCell>
                        )}

                        {/* Profit Center (Multi-Corp Daily when hasProfitCenter) */}
                        {isMultiCorp && isDaily && hasProfitCenter && (
                          <TableCell align="left" sx={cellBaseStyle}>
                            {row.pcName || '-'}
                          </TableCell>
                        )}

                        {/* Mandatory 1: Date (Only for Single Corp Daily report) */}
                        {isDaily && !isMultiCorp && (
                          <TableCell align="left" sx={cellBaseStyle}>
                            {row.date}
                          </TableCell>
                        )}

                        {/* Mandatory 2: Employee Name */}
                        <TableCell align="left" sx={cellBaseStyle}>
                          {row.employeeName}
                        </TableCell>

                        {/* Department (Multi-Corp Weekly or Multi-Corp Daily) */}
                        {((reportType === 'weekly' && isMultiCorp) || (isDaily && isMultiCorp)) && (
                          <TableCell align="left" sx={cellBaseStyle}>
                            {row.department || '-'}
                          </TableCell>
                        )}

                        {/* Job Title (Multi-Corp Weekly or Multi-Corp Daily) */}
                        {((reportType === 'weekly' && isMultiCorp) || (isDaily && isMultiCorp)) && (
                          <TableCell align="left" sx={cellBaseStyle}>
                            {row.jobTitle || '-'}
                          </TableCell>
                        )}

                        {/* Optional: Punch In (Only for Daily report) */}
                        {isDaily && activeOptionalColumns.some((c) => c.id === 'punchIn') && (
                          <TableCell align="center" sx={cellBaseStyle}>
                            {row.punchIn || '-'}
                          </TableCell>
                        )}

                        {/* Optional: Punch Out (Only for Daily report) */}
                        {isDaily && activeOptionalColumns.some((c) => c.id === 'punchOut') && (
                          <TableCell align="center" sx={cellBaseStyle}>
                            {row.punchOut || '-'}
                          </TableCell>
                        )}

                        {/* Optional 0a: Scheduled Hrs */}
                        {activeOptionalColumns.some((c) => c.id === 'scheduledHrs') && (
                          <TableCell align="right" sx={cellBaseStyle}>
                            {Number(row.scheduledHrs || 0).toFixed(2)}
                          </TableCell>
                        )}

                        {/* Optional 0b: Actual Hours */}
                        {activeOptionalColumns.some((c) => c.id === 'actualHrs') && (
                          <TableCell align="right" sx={cellBaseStyle}>
                            {Number(row.actualHrs || 0).toFixed(2)}
                          </TableCell>
                        )}

                        {/* Optional 1: Overtime Hrs */}
                        {activeOptionalColumns.some((c) => c.id === 'overtimeHrs') && (
                          <TableCell align="right" sx={cellBaseStyle}>
                            {Number(row.overtimeHrs || 0).toFixed(2)}
                          </TableCell>
                        )}

                        {/* Optional 2: Variance Hrs */}
                        {activeOptionalColumns.some((c) => c.id === 'varianceHrs') && (
                          <TableCell align="right" sx={cellBaseStyle}>
                            {Number(row.varianceHrs || 0).toFixed(2)}
                          </TableCell>
                        )}

                        {/* Optional 2b: Variance Hours /OT Hrs (Multi Corp Daily) */}
                        {activeOptionalColumns.some((c) => c.id === 'varianceOvertimeHrs') && (
                          <TableCell align="right" sx={cellBaseStyle}>
                            {Number(row.varianceHrs || 0).toFixed(2)}
                          </TableCell>
                        )}

                        {/* Optional 3: OT % (Only for Weekly & Monthly) */}
                        {activeOptionalColumns.some((c) => c.id === 'otPercent') && (
                          <TableCell align="right" sx={cellBaseStyle}>
                            {row.otPercent || '0.00%'}
                          </TableCell>
                        )}

                        {/* Optional 4: OT Risk (Only for Weekly & Monthly) */}
                        {activeOptionalColumns.some((c) => c.id === 'otRisk') && (
                          <TableCell align="center" sx={cellBaseStyle}>
                            {row.otRisk || '-'}
                          </TableCell>
                        )}

                        {/* Optional 5: Status (Only for Daily) */}
                        {activeOptionalColumns.some((c) => c.id === 'status') && (
                          <TableCell align="center" sx={cellBaseStyle}>
                            {row.status || '-'}
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}

                  {/* Group Summary / Total Row */}
                  {group.records.length > 0 && (
                    <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                      <TableCell colSpan={footerColSpan} align="left" sx={{ ...footerCellStyle, fontWeight: 700, backgroundColor: '#f8fafc' }}>
                        Total / Summary ({group.records.length} Records)
                      </TableCell>
                      {activeOptionalColumns.some((c) => c.id === 'scheduledHrs') && (
                        <TableCell align="right" sx={{ ...footerCellStyle, fontWeight: 700, backgroundColor: '#f8fafc' }}>
                          {groupTotals.scheduledHrs.toFixed(2)}
                        </TableCell>
                      )}
                      {activeOptionalColumns.some((c) => c.id === 'actualHrs') && (
                        <TableCell align="right" sx={{ ...footerCellStyle, fontWeight: 700, backgroundColor: '#f8fafc' }}>
                          {groupTotals.actualHrs.toFixed(2)}
                        </TableCell>
                      )}
                      {activeOptionalColumns.some((c) => c.id === 'overtimeHrs') && (
                        <TableCell align="right" sx={{ ...footerCellStyle, fontWeight: 700, backgroundColor: '#f8fafc' }}>
                          {groupTotals.overtimeHrs.toFixed(2)}
                        </TableCell>
                      )}
                      {activeOptionalColumns.some((c) => c.id === 'varianceHrs') && (
                        <TableCell align="right" sx={{ ...footerCellStyle, fontWeight: 700, backgroundColor: '#f8fafc' }}>
                          {groupTotals.varianceHrs.toFixed(2)}
                        </TableCell>
                      )}
                      {activeOptionalColumns.some((c) => c.id === 'varianceOvertimeHrs') && (
                        <TableCell align="right" sx={{ ...footerCellStyle, fontWeight: 700, backgroundColor: '#f8fafc' }}>
                          {groupTotals.varianceHrs.toFixed(2)}
                        </TableCell>
                      )}
                      {activeOptionalColumns.some((c) => c.id === 'otPercent') && (
                        <TableCell align="right" sx={{ ...footerCellStyle, fontWeight: 700, backgroundColor: '#f8fafc' }}>
                          {groupTotals.scheduledHrs > 0
                            ? `${((groupTotals.overtimeHrs / groupTotals.scheduledHrs) * 100).toFixed(2)}%`
                            : '0.00%'}
                        </TableCell>
                      )}
                      {activeOptionalColumns.some((c) => c.id === 'otRisk') && (
                        <TableCell align="center" sx={{ ...footerCellStyle, fontWeight: 700, backgroundColor: '#f8fafc' }}>
                          -
                        </TableCell>
                      )}
                      {activeOptionalColumns.some((c) => c.id === 'status') && (
                        <TableCell align="center" sx={{ ...footerCellStyle, fontWeight: 700, backgroundColor: '#f8fafc' }}>
                          -
                        </TableCell>
                      )}
                    </TableRow>
                  )}
                </Fragment>
              );
            })}

            {/* Grand Total Summary Row across all tables */}
            {allRecords.length > 0 && tableGroups.length > 1 && (
              <TableRow sx={{ backgroundColor: '#e2e8f0' }}>
                <TableCell colSpan={footerColSpan} align="left" sx={grandTotalCellStyle}>
                  Grand Total ({allRecords.length} Records)
                </TableCell>
                {activeOptionalColumns.some((c) => c.id === 'scheduledHrs') && (
                  <TableCell align="right" sx={grandTotalCellStyle}>
                    {grandTotals.scheduledHrs.toFixed(2)}
                  </TableCell>
                )}
                {activeOptionalColumns.some((c) => c.id === 'actualHrs') && (
                  <TableCell align="right" sx={grandTotalCellStyle}>
                    {grandTotals.actualHrs.toFixed(2)}
                  </TableCell>
                )}
                {activeOptionalColumns.some((c) => c.id === 'overtimeHrs') && (
                  <TableCell align="right" sx={grandTotalCellStyle}>
                    {grandTotals.overtimeHrs.toFixed(2)}
                  </TableCell>
                )}
                {activeOptionalColumns.some((c) => c.id === 'varianceHrs') && (
                  <TableCell align="right" sx={grandTotalCellStyle}>
                    {grandTotals.varianceHrs.toFixed(2)}
                  </TableCell>
                )}
                {activeOptionalColumns.some((c) => c.id === 'varianceOvertimeHrs') && (
                  <TableCell align="right" sx={grandTotalCellStyle}>
                    {grandTotals.varianceHrs.toFixed(2)}
                  </TableCell>
                )}
                {activeOptionalColumns.some((c) => c.id === 'otPercent') && (
                  <TableCell align="right" sx={grandTotalCellStyle}>
                    {grandTotals.scheduledHrs > 0
                      ? `${((grandTotals.overtimeHrs / grandTotals.scheduledHrs) * 100).toFixed(2)}%`
                      : '0.00%'}
                  </TableCell>
                )}
                {activeOptionalColumns.some((c) => c.id === 'otRisk') && (
                  <TableCell align="center" sx={grandTotalCellStyle}>
                    -
                  </TableCell>
                )}
                {activeOptionalColumns.some((c) => c.id === 'status') && (
                  <TableCell align="center" sx={grandTotalCellStyle}>
                    -
                  </TableCell>
                )}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default memo(SingleCorpTable);
